import React, { useState, useRef } from 'react'
import { Alert, Input, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { useGeneralToast } from '../hooks/toast'
import { checkNewAgamiManualCode, checkExistingAgamiManualCode } from '../utils/helpers/supabase.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiUploadFormData } from './UploadAgamiInterface'

const DEBOUNCE_TIMEOUT = 500

interface SpotManualCodeSelectorProps {
  formData: AgamiUploadFormData
  uploading: boolean
  handleDataChange: <K extends keyof AgamiUploadFormData>(key: K, newValue: AgamiUploadFormData[K]) => void
  spotId?: string
}

const SpotManualCodeSelector: React.FC<SpotManualCodeSelectorProps> = ({
  formData,
  uploading,
  handleDataChange,
  spotId,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const toast = useGeneralToast()
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [debounce, setDebounce] = useState(formData.manual_code)
  const [code, setCode] = useState(formData.manual_code)
  const [codeUnavailable, setCodeUnavailable] = useState(false)
  const [syntaxError, setSyntaxError] = useState(false)

  const checkCodeAvailable = async () => {
    if (!code) {
      handleDataChange('manual_code', '')
      setCodeUnavailable(false)
      setSyntaxError(false)
      return
    }

    if (code.includes('§')) {
      setSyntaxError(true)
      return
    }

    setSyntaxError(false)

    const { error, available } = spotId
      ? await checkExistingAgamiManualCode({
          spotId,
          code,
          experiences: formData.experiences,
        })
      : await checkNewAgamiManualCode({
          code,
          experiences: formData.experiences,
        })

    if (error) {
      toast.error(`Error checking manual code [${error}]`)
      return
    }

    if (!available) {
      setCodeUnavailable(true)
      handleDataChange('manual_code', '')
      return
    }

    setCodeUnavailable(false)
    handleDataChange('manual_code', code)
  }

  React.useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
    debounceTimeoutRef.current = setTimeout(() => setCode(debounce), DEBOUNCE_TIMEOUT)
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
    }
  }, [debounce])

  React.useEffect(() => {
    checkCodeAvailable()
    return () => {
      // TODO - abort API call
    }
  }, [code, spotId, formData.experiences])

  return (
    <>
      <Typography className="font-bold text-lg font-barlow" data-testid="agami-manual-code-title">
        {t('upload.manualPlaybackCode')}
      </Typography>
      <Input
        {...(darkMode && { color: 'white' })}
        className="w-full"
        value={debounce ?? ''}
        onChange={(e) => setDebounce(e.currentTarget.value)}
        type="text"
        label={t('upload.manualCode')}
        required
        disabled={uploading}
        error={codeUnavailable}
        data-testid="agami-manual-code-input"
      />

      {codeUnavailable && (
        <Alert color="red" variant="ghost">
          {t('upload.codeAlreadyUsed', { code })}
        </Alert>
      )}

      {syntaxError && (
        <Alert color="red" variant="ghost">
          {t('upload.codeSyntaxError')}
        </Alert>
      )}
    </>
  )
}

export default SpotManualCodeSelector
